import dynamic from 'next/dynamic';
import React, { useEffect, useState } from 'react';
import type { SponsoredComponentProps } from '@marty-js/design/src/molecules/sponsored-component';
import type { FeaturedItemProps } from '@marty-js/design/src/molecules/featured-item';
import type { DiscoveryItemProps } from '@marty-js/design/src/molecules/discovery-item';

import styled from 'styled-components';
import { toDiscoveryItemProps, toFeaturedItemProps, toSponsoredComponentProps } from './componentReducer';
import {
  ComponentProps,
  DiscoveryOfMoment,
  SponsoData,
  FeaturedItem as FeaturedItemType,
  ComponentPropsQuery,
} from '../types';
import { useIsMobile } from './deviceHook';
import type { DataZone } from '../ads/types';
import { BreadcrumbItem } from '../atoms/breadcrumb';

const DiscoveryItem = dynamic(() => import('@marty-js/design/src/molecules/discovery-item'));
const SponsoredComponent = dynamic(() => import('@marty-js/design/src/molecules/sponsored-component'));
const FeaturedItem = dynamic(() => import('@marty-js/design/src/molecules/featured-item'));

export type DataType = DiscoveryOfMoment | SponsoData | FeaturedItemType;
export type TemplateType = 'discovery_of_the_moment' | 'software_modal_3_columns' | 'featured_item';
type TemplateComponentProps = DiscoveryItemProps | SponsoredComponentProps | FeaturedItemProps;

const SPONSO_CONTEXT_PROPS: {
  [key in TemplateType]: (data: DataType) => TemplateComponentProps;
} = {
  discovery_of_the_moment: toDiscoveryItemProps,
  software_modal_3_columns: toSponsoredComponentProps,
  featured_item: toFeaturedItemProps,
};

type SponsoFactoryProps = {
  zone: DataZone<ComponentProps<DataType>, TemplateType>;
  showMobile?: boolean;
  utm?: {
    breadcrumb?: BreadcrumbItem[];
    title?: string;
  };
};

const SidebarSponsoredLoading = styled.div`
  margin-top: 30px;
  height: 270px;
  background: var(--color-alt-2);
`;

export const SponsoFactory = ({ zone, showMobile = false }: SponsoFactoryProps) => {
  const component = zone?.component;
  const [data, setData] = useState<TemplateComponentProps>();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!component?.template || !component?.data) {
      return;
    }
    const transformFunction = SPONSO_CONTEXT_PROPS[component.template];

    if (transformFunction) {
      const sponsoData = transformFunction(component.data as ComponentPropsQuery);
      if (zone?.impressionCount && (!isMobile || showMobile)) {
        zone.impressionCount();
      }

      setData(sponsoData);
    }
  }, [zone, component, isMobile, showMobile]);

  if (!data || (isMobile && !showMobile)) {
    return null;
  }

  switch (component.template) {
    case 'featured_item':
      return <FeaturedItem {...data} onClick={zone.clickRef} />;
    case 'discovery_of_the_moment':
      return <DiscoveryItem {...data} onClick={zone.clickRef} hiddenMobile />;
    case 'software_modal_3_columns':
      return <SponsoredComponent {...(data as SponsoredComponentProps)} onClick={zone.clickRef} hiddenOnMobile />;
    default:
  }

  return <SidebarSponsoredLoading />;
};
